import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "../api";
import { useAuth } from "../auth";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Spinner } from "../components/ui/spinner";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const MINT = "#aaffdc";
const CYAN = "#81ecff";

export default function Releases() {
  const { active } = useAuth();
  const { data: releases, isLoading } = useQuery({
    queryKey: ["releases", active?.slug],
    queryFn: () => endpoints.listReleases(active!.slug),
    enabled: !!active,
  });

  return (
    <div className="space-y-6 pb-12 px-8 pt-8">
      <div>
        <h1 className="text-foreground uppercase" style={{ fontSize: "20px", letterSpacing: "0.02em" }}>Releases</h1>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Releases are created in NOX Private. Manage smartlinks, analytics, and DJ promo from here.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <Spinner />
          ) : releases && releases.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[52px]"></TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Artist</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {releases.map((r) => {
                  // Prefer release_date (the meaningful "this is when fans
                  // get it" date). Fall back to created_at so the column
                  // never reads blank. Both come back as strings or null.
                  const date = r.release_date ?? (r.created_at ? r.created_at.slice(0, 10) : null);
                  return (
                  <TableRow key={r.id}>
                    <TableCell className="w-[52px]"><Cover url={r.artwork_url} /></TableCell>
                    <TableCell>{r.title}</TableCell>
                    <TableCell><span style={{ color: "#adaaaa" }}>{r.artist || "—"}</span></TableCell>
                    <TableCell><StageBadge stage={r.stage} /></TableCell>
                    <TableCell>
                      {date ? (
                        <span style={{ backgroundColor: "#262626", color: CYAN, padding: "2px 8px", borderRadius: 4, fontSize: "0.75rem", fontWeight: 400 }}>
                          {date}
                        </span>
                      ) : "—"}
                    </TableCell>
                    <TableCell>
                      <Link to={`/app/releases/${r.slug ?? r.id}`}>
                        <Button variant="default" size="sm">Manage</Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <p className="text-muted-foreground text-sm text-center py-12">
              No releases yet. Create them in NOX Private — they'll show here automatically.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Cover({ url }: { url: string | null }) {
  return (
    <div
      className="rounded overflow-hidden border border-border bg-muted shrink-0"
      style={{ width: 36, height: 36, minWidth: 36, minHeight: 36 }}
    >
      {url && (
        <img
          src={url}
          alt=""
          className="block w-full h-full object-cover"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
        />
      )}
    </div>
  );
}

function StageBadge({ stage }: { stage: string }) {
  if (stage === "promo") {
    return (
      <span style={{ backgroundColor: "#262626", color: MINT, padding: "2px 8px", borderRadius: 4, fontSize: "0.75rem", fontWeight: 400 }}>
        promo
      </span>
    );
  }
  if (stage === "released") {
    return <Badge variant="purple">released</Badge>;
  }
  return <Badge variant="secondary">{stage}</Badge>;
}
